import { useEffect, useState } from "react";
import type {
  ProductCategory,
  ProductCategoryDraft,
} from "@/pages/admin/products/hooks/useAdminProducts";

interface CategoryManagerProps {
  categories: ProductCategory[];
  productsCountByCategory: Record<string, number>;
  activeCategory?: string;
  onFilterByCategory?: (name: string) => void;
  onSave: (draft: ProductCategoryDraft) => Promise<void>;
  onDelete: (category: ProductCategory) => Promise<void>;
  notify: (text: string, tone?: "success" | "error") => void;
}

interface RowProps {
  category: ProductCategory;
  productCount: number;
  active: boolean;
  onFilter: (name: string) => void;
  onSave: (draft: ProductCategoryDraft) => Promise<void>;
  onDelete: (category: ProductCategory) => Promise<void>;
  notify: (text: string, tone?: "success" | "error") => void;
}

function CategoryRow({ category, productCount, active, onFilter, onSave, onDelete, notify }: RowProps) {
  const [name, setName] = useState(category.name);
  const [sortOrder, setSortOrder] = useState(String(category.sort_order));
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setName(category.name);
    setSortOrder(String(category.sort_order));
  }, [category]);

  const dirty = name.trim() !== category.name || String(Number(sortOrder) || 0) !== String(category.sort_order);

  const handleSave = async () => {
    if (!name.trim()) {
      notify("分类名称不能为空", "error");
      return;
    }
    setSaving(true);
    try {
      await onSave({ id: category.id, name: name.trim(), sort_order: Number(sortOrder) || 0 });
      notify(
        name.trim() !== category.name ? `已将分类「${category.name}」重命名为「${name.trim()}」` : "已更新分类排序",
        "success",
      );
    } catch (err) {
      notify(err instanceof Error ? err.message : "保存分类失败", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await onDelete(category);
      notify(`已删除分类「${category.name}」`, "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : "删除分类失败", "error");
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  };

  return (
    <li className="flex flex-col gap-2 rounded-md border border-background-200 bg-background-50 p-3 sm:flex-row sm:items-center">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
        <i className="ri-price-tag-3-line" />
      </span>
      <input
        type="text"
        value={name}
        onChange={(event) => setName(event.target.value)}
        aria-label="分类名称"
        className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none sm:flex-1"
      />
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5 text-[11px] text-foreground-500">
          排序
          <input
            type="number"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
            aria-label="排序值"
            className="w-16 rounded-md border border-background-300 bg-background-50 px-2 py-1.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          />
        </label>
        <button
          type="button"
          onClick={() => onFilter(category.name)}
          title={`点击查看「${category.name}」分类下的产品`}
          className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-[11px] transition-colors ${
            active
              ? "bg-secondary-500 font-medium text-background-50"
              : "bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900"
          }`}
        >
          <span className="flex h-3 w-3 items-center justify-center">
            <i className={active ? "ri-filter-fill" : "ri-filter-3-line"} />
          </span>
          {productCount} 款产品
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !dirty}
          className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-2.5 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-save-line" />
          </span>
          保存
        </button>

        {confirming ? (
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              className="cursor-pointer whitespace-nowrap rounded-md bg-accent-600 px-2.5 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-accent-700 disabled:opacity-60"
            >
              确认删除
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-2 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-background-100"
            >
              取消
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            aria-label="删除分类"
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-500 transition-colors hover:border-accent-300 hover:bg-accent-50 hover:text-accent-700"
          >
            <i className="ri-delete-bin-line" />
          </button>
        )}
      </div>
    </li>
  );
}

export default function CategoryManager({
  categories,
  productsCountByCategory,
  activeCategory,
  onFilterByCategory,
  onSave,
  onDelete,
  notify,
}: CategoryManagerProps) {
  const [open, setOpen] = useState(true);
  const [newName, setNewName] = useState("");
  const [newSort, setNewSort] = useState("");
  const [adding, setAdding] = useState(false);

  const nextSort = categories.length > 0 ? Math.max(...categories.map((item) => item.sort_order)) + 1 : 1;

  const handleAdd = async () => {
    if (!newName.trim()) {
      notify("请填写分类名称", "error");
      return;
    }
    setAdding(true);
    try {
      await onSave({ name: newName.trim(), sort_order: Number(newSort) || nextSort });
      notify(`已新增分类「${newName.trim()}」`, "success");
      setNewName("");
      setNewSort("");
    } catch (err) {
      notify(err instanceof Error ? err.message : "新增分类失败", "error");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-100 p-5">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full cursor-pointer items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
            <i className="ri-node-tree" />
          </span>
          <div>
            <h2 className="font-heading text-base font-semibold text-foreground-950">产品分类管理</h2>
            <p className="mt-0.5 text-xs text-foreground-500">
              维护分类名称与排序，共 {categories.length} 个分类；产品编辑时的分类从此清单下拉选择，避免手输不一致。
            </p>
          </div>
        </div>
        <span className="flex h-6 w-6 items-center justify-center text-foreground-500">
          <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
        </span>
      </button>

      {open && (
        <div className="mt-4">
          <div className="flex flex-col gap-2 rounded-md border border-background-200 bg-background-50 p-3 sm:flex-row sm:items-center">
            <input
              type="text"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="新增分类名称，例如：安全工具"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none sm:flex-1"
            />
            <input
              type="number"
              value={newSort}
              onChange={(event) => setNewSort(event.target.value)}
              placeholder={`排序（默认 ${nextSort}）`}
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none sm:w-40"
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={adding}
              className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-add-line" />
              </span>
              添加分类
            </button>
          </div>

          {categories.length === 0 ? (
            <p className="mt-3 rounded-md border border-dashed border-background-300 py-6 text-center text-xs text-foreground-500">
              还没有分类，先在上方添加一个吧
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {categories.map((category) => (
                <CategoryRow
                  key={category.id}
                  category={category}
                  productCount={productsCountByCategory[category.name] ?? 0}
                  active={activeCategory === category.name}
                  onFilter={(name) => onFilterByCategory?.(name)}
                  onSave={onSave}
                  onDelete={onDelete}
                  notify={notify}
                />
              ))}
            </ul>
          )}

          <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-foreground-500">
            <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center text-secondary-700">
              <i className="ri-information-line" />
            </span>
            重命名分类会同步更新归在该分类下的产品；删除分类不会清除产品上的分类文本，便于日后恢复。
          </p>
        </div>
      )}
    </div>
  );
}